import Service, { inject as service } from '@ember/service';
import { task, restartableTaskGroup } from 'ember-concurrency';
import { STORAGE_KEYS } from 'sh-www/services/local-storage-fallback';
import config from 'sh-www/config/environment';

class ShAuthService extends Service {
  @service api;
  @service cookies;
  @service session;
  @service('local-storage-fallback') storage;

  @restartableTaskGroup auth;

  /**
   * @param {string} username
   * @param {string} password
   * @return {Promise<*>}
   */
  login(username, password) {
    return this.authenticate.perform(() => {
      return this.api.post('auth/login', { jsonData: { email: username, password } });
    });
  }

  /**
   * @param {string} invite_code
   * @return {Promise<*>}
   */
  acceptInvite(invite_code) {
    return this.authenticate.perform(() => {
      return this.api.post('invites/accept-invite', { jsonData: { invite_code } });
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.email
   * @param {string=} params.password
   * @param {number} params.type
   * @return {Promise<*>}
   */
  createUser(params) {
    return this.authenticate.perform(() => {
      return this.api.post('users', { jsonData: { ...params, 'legal_accept': 1 } });
    });
  }

  /**
   * Login with a Google jwt credential
   * @param credential
   * @returns {*}
   */
  loginGoogleJwt(credential) {
    return this.authenticate.perform(() => {
      return this.api.post('oauth/google-jwt/login', { jsonData: { credential } });
    });
  }

  /**
   * Login with a Google jwt credential
   * @param credential
   * @param type
   * @returns {*}
   */
  signupGoogleJwt(credential, type) {
    return this.authenticate.perform(() => {
      return this.api.post(
        'oauth/google-jwt/signup', {
          jsonData: {
            credential,
            type,
          }
        }
      );
    });
  }

  /**
   * @param {string} token
   * @param {string=} opt_new_email
   * @return {Promise<*>}
   */
  verifyEmail(token, opt_new_email) {
    return this.authenticate.perform(() => {
      const jsonData = { token };

      if (opt_new_email) {
        jsonData.new_email = opt_new_email;
      }

      return this.api.post(`email/verify`, { jsonData });
    });
  }

  /**
   * @param {string} email
   * @param {string} new_password
   * @param {string} new_password_confirmation
   * @param {string=} opt_old_password
   * @return {Promise<*>}
   */
  updatePassword(email, new_password, new_password_confirmation, opt_old_password) {
    return this.authenticate.perform(() => {
      const jsonData = {
        email,
        new_password,
        new_password_confirmation,
      };

      if (opt_old_password) {
        jsonData.password = opt_old_password;
      }

      return this.api.post('password/update', { jsonData });
    });
  }

  /**
   * @param {string} token
   * @param {string} email
   * @param {string} password
   * @param {string} password_confirmation
   * @return {Promise<*>}
   */
  resetPassword(token, email, password, password_confirmation) {
    return this.authenticate.perform(() => {
      return this.api.post('password/reset', { jsonData: { token, email, password, password_confirmation } })
    });
  }

  /**
   * @param {string|number} targetUserId
   */
  @task({ group: 'auth' })
  *masquerade(targetUserId) {
    if (config.DEBUG) {
      console.log(`AUTH - Attempting Masquerade`);
    }

    yield this.api.post(`auth/${targetUserId}/masquerade`);

    this.storage.setItem(STORAGE_KEYS.MASQUERADE_ORIG_AUTH, '1');

    yield this.session.authenticate('authenticator:simple', { });

    window.location.replace('/start');
  }

  @task({ group: 'auth' })
  *unMasquerade() {
    if (config.DEBUG) {
      console.log(`AUTH - Ending Masquerade`);
    }

    yield this.api.post(`auth/un-masquerade`);

    this.storage.removeItem(STORAGE_KEYS.MASQUERADE_ORIG_AUTH);

    /**
     * This looks wrong but it is actually correct. We are un-masquerading, which means we are
     * effectively re-authenticating as the admin as far as ember is concerned.
     */
    yield this.session.authenticate('authenticator:simple', { });

    window.location.replace('/start');
  }

  @task({ group: 'auth' })
  *authenticate(f) {
    yield this.session.authenticate('authenticator:simple', yield f());
  }
}

export default ShAuthService;
